import { useEffect, useRef, useState } from 'react';
import { useField } from 'formik';
import { useAppDispatch } from 'commons/store/hooks';
import { flatten, isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { putElementData } from "../features/ecrf/redux/elementDataSlice";
import { updateElementDataCollection } from "../features/ecrf/redux/elementDataCollectionSlice";

type UseAutoSaveFieldFormikProps = {
  name: string;
  label?: string;
  dataId?: string;
  rowId?: string;
  disabled?: boolean;
  saveRow?: boolean;
  reloadData?: () => void;
};

export const useAutoSaveFieldFormik = ({
  name,
  label,
  dataId,
  rowId,
  disabled,
  saveRow,
  reloadData,
}: UseAutoSaveFieldFormikProps) => {
  const [field] = useField(name);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const lastSavedValue = useRef<any>(field.value);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (disabled || saveRow === true || isEqual(lastSavedValue.current, field.value)) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      setLoading(true);

      try {
        if (dataId) {
          await dispatch(putElementData({ id: dataId, params: { data: flatten([field.value]) } }));
        } else if (rowId) {
          await dispatch(
            updateElementDataCollection({
              id: rowId,
              params: {
                elements: [
                  {
                    ecrf: `/api/ecrves/${id}`,
                    element: `/api/elements/${name}`,
                    data: flatten([field.value]),
                  },
                ],
              }
            })
          );
        }

        if (reloadData) await reloadData();

        enqueueSnackbar(`${t('saved')} ${label || name} ${new Date().toLocaleTimeString()}`, {
          variant: 'info',
          autoHideDuration: 2000,
        });

        lastSavedValue.current = field.value;
      } catch (e: any) {
        enqueueSnackbar(e?.detail || e?.message || 'Save error', { variant: 'error' });
      }

      setLoading(false);
    }, 1000); // debounce 1s

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [field.value]);

  return { loading };
};